import type { RomMeta } from './romRepository';
import {
  PSX_MEMCARD_BYTES,
  saveSram,
  saveState,
  sramKeyFor,
} from './saveRepository';
import { preferredBackendIdFor } from '../emulator/registry';
import { clearTombstone } from './libraryTombstones';

export interface ImportSaveResult {
  sram: number;
  states: number;
  skipped: string[];
}

function basename(name: string): string {
  return name.replace(/^.*[\\/]/, '').toLowerCase();
}

/**
 * ブラウザが付ける ` (1)` や `.download` を落としてから判定する。
 * 例: `game.state1 (1).bin` → `game.state1.bin`
 */
function normalizeSaveFilename(name: string): string {
  return basename(name)
    .replace(/\.download$/i, '')
    .replace(/\s*\(\d+\)\s*(?=\.[^.]+$)/, '');
}

/**
 * ファイル名からステートスロットを拾う。
 *
 * - アプリ書き出し: `….state1.bin`（1 始まり → スロット 0）
 * - Azure / サーバー blob: `state-0.bin` / `….state-1.bin`（ハイフン付きは 0 始まり）
 */
export function stateSlotFromName(name: string): number | null {
  const n = normalizeSaveFilename(name);

  // サーバー blob 形式（ハイフンあり）はスロット番号そのもの
  const blobStyle = n.match(/(?:^|\.)state-(-?\d{1,2})(?:\.bin)?$/i);
  if (blobStyle) {
    const slot = Number(blobStyle[1]);
    if (slot >= -1 && slot <= 9) return slot;
    return null;
  }

  // アプリの「セーブデータをダウンロード」形式（ハイフンなし・1 始まり）
  const exportStyle = n.match(/(?:^|\.)state(\d{1,2})(?:\.bin)?$/i);
  if (exportStyle) {
    const num = Number(exportStyle[1]);
    if (num >= 1 && num <= 10) return num - 1;
    if (num === 0) return 0;
  }

  return null;
}

function looksLikeSram(name: string): boolean {
  const n = normalizeSaveFilename(name);
  return (
    /\.srm$/i.test(n) ||
    /\.sav$/i.test(n) ||
    /\.sram(\.bin)?$/i.test(n) ||
    /\.memcard(\.bin)?$/i.test(n) ||
    /\.mcd$/i.test(n) ||
    /\.mcr$/i.test(n)
  );
}

async function readFileBytes(file: File): Promise<Uint8Array> {
  const buf = await file.arrayBuffer();
  return new Uint8Array(buf);
}

/**
 * 外部セーブファイルをこの ROM（または PS1 共有カード）へ取り込む。
 * 対応: `.srm` / `.sav` / `.sram.bin` / `.memcard.bin` / `.mcd` / `.mcr`、
 * アプリ書き出しの `.stateN.bin`、Azure blob の `state-N.bin`。
 */
export async function importSaveFiles(rom: RomMeta, files: File[]): Promise<ImportSaveResult> {
  if (files.length === 0) throw new Error('ファイルが選ばれていません');

  const result: ImportSaveResult = { sram: 0, states: 0, skipped: [] };
  const backendId = preferredBackendIdFor(rom.system);
  const sramKey = sramKeyFor(rom.system, rom.id);

  for (const file of files) {
    const name = file.name;
    const slot = stateSlotFromName(name);
    if (slot !== null) {
      const data = await readFileBytes(file);
      if (data.byteLength === 0) {
        result.skipped.push(`${name}（空）`);
        continue;
      }
      await saveState({ romId: rom.id, slot, data, backendId });
      result.states += 1;
      continue;
    }

    if (looksLikeSram(name)) {
      const data = await readFileBytes(file);
      if (data.byteLength === 0) {
        result.skipped.push(`${name}（空）`);
        continue;
      }
      if (rom.system === 'psx' && data.byteLength !== PSX_MEMCARD_BYTES) {
        result.skipped.push(
          `${name}（PS1 カードは ${PSX_MEMCARD_BYTES} バイト。実際は ${data.byteLength}）`,
        );
        continue;
      }
      await saveSram(sramKey, data);
      result.sram += 1;
      continue;
    }

    result.skipped.push(`${name}（形式が不明）`);
  }

  if (result.sram === 0 && result.states === 0) {
    const detail = result.skipped.length ? `（${result.skipped.join('、')}）` : '';
    throw new Error(`取り込めるセーブがありません${detail}`);
  }

  // 同期でサーバー側の墓標がこの ROM を消さないようにする（取り込み直後に消える主因）
  await clearTombstone(rom.sha1);

  return result;
}
